<template>
  <div class="absence-form">
    <div class="row g-2">
      <div class="col-12">
        <label class="form-label text-xs mb-0">{{ $t('timeRegistration.type') }}</label>
        <select v-model="form.typeId" class="form-select form-select-sm">
          <option v-for="t in absenceTypes" :key="t.id" :value="t.id">{{ absenceLabel(t.type) }}</option>
        </select>
      </div>
      <div class="col-6">
        <label class="form-label text-xs mb-0">{{ $t('timeRegistration.fromDate') }}</label>
        <input v-model="form.from" type="date" class="form-control form-control-sm" @change="reload" />
      </div>
      <div class="col-6">
        <label class="form-label text-xs mb-0">{{ $t('timeRegistration.toDate') }} ({{ $t('common.optional') }})</label>
        <input v-model="form.to" type="date" class="form-control form-control-sm" :min="form.from" />
      </div>

      <div v-if="isRange" class="col-12">
        <p class="text-xs text-secondary mb-0">{{ $t('endOfDay.absenceRangeFullDays') }}</p>
      </div>
      <div v-else-if="day.loading" class="col-12 text-center py-2">
        <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
      </div>
      <div v-else-if="blockedReason" class="col-12">
        <div class="alert alert-light border text-sm py-2 mb-0">
          <i class="material-icons align-middle me-1 text-warning" style="font-size:1rem">info</i>{{ blockedReason }}
        </div>
      </div>
      <template v-else>
        <div class="col-12 mt-3">
          <span class="section-label d-block mb-1">{{ $t('endOfDay.absenceDuration') }}</span>
          <!-- A day with registrations only takes part of the day, so there is nothing to choose -->
          <template v-if="fullDayAllowed">
            <div class="duration-toggle" role="group">
              <button type="button" class="btn mb-0" :class="form.fullDay ? 'btn-dark' : 'btn-outline-secondary'" @click="form.fullDay = true">
                {{ $t('endOfDay.absenceFullDay') }}
              </button>
              <button type="button" class="btn mb-0" :class="form.fullDay ? 'btn-outline-secondary' : 'btn-dark'" @click="form.fullDay = false">
                {{ $t('endOfDay.absencePartDay') }}
              </button>
            </div>
            <p v-if="form.fullDay" class="text-xs text-secondary mt-1 mb-0">{{ $t('endOfDay.fullDayScheduled') }}: {{ fmt(day.scheduled) }} {{ $t('endOfDay.hoursUnit') }}</p>
          </template>
        </div>
        <div v-if="!form.fullDay" class="col-12 mt-2">
          <div class="stepper">
            <button type="button" class="btn btn-outline-secondary mb-0" :disabled="form.hours <= minHours" @click="step(-1)">−1</button>
            <button type="button" class="btn btn-outline-secondary mb-0" :disabled="form.hours <= minHours" @click="step(-0.5)">−½</button>
            <div class="stepper-value"><span>{{ fmt(form.hours) }}</span><small>{{ $t('endOfDay.hoursUnit') }}</small></div>
            <button type="button" class="btn btn-outline-secondary mb-0" :disabled="form.hours >= room" @click="step(0.5)">+½</button>
            <button type="button" class="btn btn-outline-secondary mb-0" :disabled="form.hours >= room" @click="step(1)">+1</button>
          </div>
          <p class="text-xs mt-2 mb-0" :class="form.hours >= room ? 'text-dark fw-semibold' : 'text-secondary'">
            {{ day.work > 0
              ? $t('endOfDay.absenceMaxAfterWork', { hours: fmt(room), scheduled: fmt(day.scheduled), work: fmt(day.work) })
              : $t('endOfDay.absenceMax', { hours: fmt(room) }) }}
          </p>
        </div>
      </template>
    </div>
    <div class="d-flex gap-2 mt-3">
      <button class="btn btn-sm btn-outline-secondary mb-0" @click="$emit('cancel')">{{ $t('common.cancel') }}</button>
      <button class="btn btn-sm btn-dark mb-0 ms-auto" :disabled="!canSubmit" @click="submit">
        <span v-if="form.saving" class="spinner-border spinner-border-sm me-1"></span>{{ $t('common.save') }}
      </button>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";

/**
 * AbsenceForm — registers an absence on end-of-day registration (EnableEndOfDayTimeRegistration).
 * Used by the worker's Skrá dag tab and by the Yfirlit absence form (admins: userId set).
 * Full-day absences are stored as the day's scheduled dagvinna (useScheduledHours) and are only
 * offered on a day with nothing registered (the server's rule). A part-day absence is capped at
 * the dagvinna the day has left: scheduled dagvinna minus the dagvinna already registered as
 * work. Ranges are full days only (the server skips weekends, holidays and registered days).
 */
export default {
  name: "AbsenceForm",
  props: {
    date: { type: String, default: "" }, // yyyy-mm-dd the form starts on
    userId: { type: String, default: null }, // admin registering for another user
    absenceTypes: { type: Array, default: () => [] },
  },
  emits: ["saved", "cancel"],
  data() {
    return {
      form: { typeId: this.absenceTypes[0]?.id ?? null, from: this.date, to: "", fullDay: true, hours: 0, saving: false },
      // What is already registered on form.from, so the form offers only what fits
      day: { loading: false, scheduled: 0, absence: 0, work: 0, hasEntries: false },
      dayRequest: 0,
      minHours: 0.25,
    };
  },
  computed: {
    isRange() { return !!this.form.to && this.form.to > this.form.from; },
    /** Dagvinna left for an absence: scheduled minus dagvinna already registered as work. */
    room() { return Math.max(0, Math.floor((this.day.scheduled - this.day.absence - this.day.work) * 4) / 4); },
    /** Why no absence fits on the chosen single day (empty when one does). */
    blockedReason() {
      const d = this.day;
      if (this.isRange || d.loading) return "";
      if (d.absence > 0) return this.$t("endOfDay.absenceAlreadyExists");
      if (d.scheduled <= 0) return this.$t("endOfDay.absenceNoSchedule");
      if (this.room <= 0) return this.$t("endOfDay.absenceDayFull", { hours: this.fmt(d.scheduled) });
      return "";
    },
    // The server only takes a full-day absence on a day with no registrations
    fullDayAllowed() { return !this.day.hasEntries; },
    canSubmit() {
      const f = this.form;
      if (f.saving || !f.typeId || !f.from || (f.to && f.to < f.from)) return false;
      if (this.isRange) return true;
      if (this.day.loading || this.blockedReason) return false;
      return f.fullDay ? this.fullDayAllowed : f.hours > 0 && f.hours <= this.room;
    },
  },
  watch: {
    userId() { this.reload(); },
  },
  created() { this.reload(); },
  methods: {
    absenceLabel(type) {
      const key = `timesheetEntryTypes.${type.charAt(0).toLowerCase()}${type.slice(1)}`;
      return this.$te(key) ? this.$t(key) : type;
    },
    fmt(h) {
      const v = Number(h) || 0;
      return v.toLocaleString(this.$i18n.locale?.value || this.$i18n.locale || "is-IS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    /** Load the from-day's schedule and registrations; pick full day on an empty day, else the dagvinna left. */
    async reload() {
      const date = this.form.from;
      if (!date) return;
      const request = ++this.dayRequest;
      const userParam = this.userId ? `&userId=${this.userId}` : "";
      this.day = { ...this.day, loading: true };
      try {
        const [planRes, entriesRes] = await Promise.all([
          api.get(`/Timesheet/day-plan?dateFrom=${date}&dateTo=${date}${userParam}`),
          api.get(`/Timesheet?dateFrom=${date}&dateTo=${date}${userParam}`),
        ]);
        if (request !== this.dayRequest) return;
        const plan = (planRes.data || [])[0] || {};
        const entries = (entriesRes.data?.days || []).flatMap(d => d.entries || []);
        // Dagvinna already worked: hours-only lines carry normalHours, clocked entries count their duration
        const work = entries.filter(e => !e.isAbsence)
          .reduce((s, e) => s + (e.normalHours != null ? Number(e.normalHours) : (e.adjustedDurationMinutes || 0) / 60), 0);
        this.day = {
          loading: false,
          scheduled: Number(plan.scheduledNormalHours) || 0,
          absence: Number(plan.absenceHours) || 0,
          work,
          hasEntries: entries.length > 0,
        };
        this.form.fullDay = !this.day.hasEntries;
        // Part of a worked day defaults to the rest of the day; otherwise half the day
        this.form.hours = work > 0 ? this.room : Math.min(this.room, Math.max(0.5, Math.round(this.room) / 2));
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
        if (request === this.dayRequest) this.day = { ...this.day, loading: false };
      }
    },
    step(delta) {
      const v = Math.min(this.room, Math.max(this.minHours, this.form.hours + delta));
      this.form.hours = Math.round(v * 4) / 4;
    },
    async submit() {
      const f = this.form;
      const body = { dateFrom: f.from, dateTo: f.to || f.from, timesheetEntryTypeId: f.typeId, ...(this.userId ? { userId: this.userId } : {}) };
      if (f.fullDay || this.isRange) body.useScheduledHours = true;
      else body.hours = f.hours;
      f.saving = true;
      try {
        const r = await api.post("/Timesheet/absence", body);
        if (r.data?.skippedDates?.length) {
          this.$toast?.warning(`${this.$t("timeRegistration.absenceDaysSkipped")}: ${r.data.skippedDates.length}`, { timeout: 5000 });
        }
        this.$emit("saved");
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.response?.data || this.$t("timeRegistration.registrationError");
        this.$toast?.danger(typeof msg === "string" ? msg : JSON.stringify(msg), { timeout: 0 });
      } finally {
        f.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.absence-form { border: 1px solid #d2d6da; border-radius: 0.6rem; padding: 0.9rem; }
.section-label { font-size: 0.75rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #67748e; }
.duration-toggle { display: flex; gap: 0.4rem; }
.duration-toggle .btn { flex: 1 1 0; padding: 0.6rem 0.5rem; font-weight: 600; text-transform: none; }
.stepper { display: flex; align-items: center; gap: 0.4rem; }
.stepper .btn { flex: 1 1 0; padding: 0.65rem 0; font-weight: 600; text-transform: none; }
.stepper-value { flex: 1.4 1 0; display: flex; flex-direction: column; align-items: center; line-height: 1; }
.stepper-value span { font-size: 1.75rem; font-weight: 700; color: #344767; }
.stepper-value small { font-size: 0.75rem; color: #67748e; }
</style>
